import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { isNurseActiveInDepartment } from "../../utils/formatters";
import { MessageStatus, type Nurse, type UserSentMessage } from "../../utils/models";
import { usePostStaffMessage, useThreadMessagesQuery, useThreadsQuery, useUpdateThreadReadStatus } from "./util/hooks";
import { combineStaffCommunicationHistory, getFilteredThreads } from "./util/utils";

const useStaffCommunication = () => {
	const { hospitalData, chosenDepartment, userIdNurseMap } = useHospitalData();

	// Get combined messages (SMS + push) and thread read statuses
	const { data: messagesData } = useThreadsQuery(chosenDepartment.department_id);
	const unreadCounts = messagesData?.unreadCounts;

	const { mutate: updateThreadReadStatus } = useUpdateThreadReadStatus(chosenDepartment.department_id);
	const { mutate: postStaffMessage } = usePostStaffMessage();

	const [selectedUserId, setSelectedUserId] = useState<Nurse["userId"]>();

	// Update last read time when a thread is opened
	useEffect(() => {
		if (selectedUserId) {
			updateThreadReadStatus(selectedUserId);
		}
	}, [selectedUserId, updateThreadReadStatus]);

	const clearUserSelection = useCallback(() => {
		setSelectedUserId(undefined);
	}, []);

	const [nurseSearchPhrase, setNurseSearchPhrase] = useState("");

	const selectedNurse = selectedUserId ? userIdNurseMap[selectedUserId] : null;
	const threadHistoryQuery = useThreadMessagesQuery(chosenDepartment.department_id, selectedUserId);

	// Convert SMS and Push to unified threads dictionary
	const smsThreads = useMemo(() => {
		if (!messagesData?.sms && !messagesData?.push) return undefined;

		const threads: Record<string, UserSentMessage[]> = {};

		// Add SMS messages
		if (messagesData?.sms) {
			for (const msg of messagesData.sms) {
				if (!threads[msg.userId]) {
					threads[msg.userId] = [];
				}
				threads[msg.userId].push(msg);
			}
		}

		// Convert and add push notifications as SMS-like messages
		if (messagesData?.push) {
			for (const push of messagesData.push) {
				if (!threads[push.userId]) {
					threads[push.userId] = [];
				}

				// Convert push notification to UserSentMessage format
				const pushAsMessage: UserSentMessage = {
					id: push.id,
					departmentId: chosenDepartment.department_id,
					userId: push.userId,
					status: push.status === "sent" ? MessageStatus.delivered : MessageStatus.failed,
					body: push.body,
					senderUserId: undefined,
					dateCreated: push.dateSent, // Map dateSent to dateCreated
					direction: "outbound",
					senderFirstName: undefined,
					isPush: true,
					platform: push.platform,
					title: push.title,
					sid: push.pushId,
				};

				threads[push.userId].push(pushAsMessage);
			}
		}

		return threads;
	}, [messagesData?.sms, messagesData?.push, chosenDepartment.department_id]);

	// Full history is loaded only after a nurse is selected; the list request carries summaries only.
	const thread = useMemo(() => {
		if (!selectedUserId || !threadHistoryQuery.data) return [];
		return combineStaffCommunicationHistory(threadHistoryQuery.data.pages, chosenDepartment.department_id);
	}, [chosenDepartment.department_id, selectedUserId, threadHistoryQuery.data]);

	const handlePostNewMessage = useCallback(
		(message: string) => {
			if (!selectedNurse || !message.trim() || !selectedNurse.userId) return;
			const userId = selectedNurse.userId;
			postStaffMessage({
				message,
				userIds: [userId],
				tempId: uuidv4(),
			});
		},
		[selectedNurse, postStaffMessage],
	);

	const [selectedFilterStatuses, setSelectedFilterStatuses] = useState<MessageStatus[]>(Object.values(MessageStatus));
	const [showUnreadOnly, setShowUnreadOnly] = useState(false);

	const filteredThreads = useMemo(
		() => (smsThreads ? getFilteredThreads(selectedFilterStatuses, smsThreads) : undefined),
		[selectedFilterStatuses, smsThreads],
	);

	const concatenatedThreads = useMemo(() => {
		return filteredThreads
			? Object.entries(filteredThreads)
					.concat(
						hospitalData.nurses
							// Active in this department (per-department source of truth), not the legacy global nurse.active.
							.filter((n) => n.userId && !filteredThreads[n.userId] && n.phoneNumber && isNurseActiveInDepartment(n, chosenDepartment.department_id))
							.map((n) => [n.userId ?? "", []]),
					)
					.map(([userId, messages]) => {
						const firstMessage = messages.length ? messages.reduce((max, msg) => (msg.dateCreated > max.dateCreated ? msg : max)) : undefined;

						// DEFAULT: no unread-count row means the thread has no unread inbound messages.
						const unreadCount = unreadCounts?.[userId] ?? 0;

						return { userId, firstMessage, unreadCount };
					})
					.filter((value) => {
						if (!(selectedFilterStatuses.length === Object.values(MessageStatus).length || value.firstMessage)) {
							return false;
						}
						if (showUnreadOnly && value.unreadCount === 0) {
							return false;
						}
						return true;
					})
					.sort((a, b) => {
						if (!a.firstMessage && !b.firstMessage) return 0;
						if (!a.firstMessage) return 1;
						if (!b.firstMessage) return -1;

						// Prioritize threads with unread messages
						if (a.unreadCount !== 0 && b.unreadCount === 0) return -1;
						if (a.unreadCount === 0 && b.unreadCount !== 0) return 1;

						// If both unread or both read, sort by most recent
						return b.firstMessage.dateCreated.valueOf() - a.firstMessage.dateCreated.valueOf();
					})
			: undefined;
	}, [filteredThreads, hospitalData.nurses, selectedFilterStatuses, unreadCounts, showUnreadOnly, chosenDepartment.department_id]);

	return {
		concatenatedThreads,
		unfilteredThreads: smsThreads,
		selectedUserId,
		setSelectedUserId,
		clearUserSelection,
		nurseSearchPhrase,
		setNurseSearchPhrase,
		selectedNurse,
		thread,
		handlePostNewMessage,
		userIdNurseMap,
		selectedFilterStatuses,
		setSelectedFilterStatuses,
		unreadCounts,
		showUnreadOnly,
		setShowUnreadOnly,
		hasOlderMessages: Boolean(threadHistoryQuery.hasNextPage),
		loadOlderMessages: threadHistoryQuery.fetchNextPage,
		isLoadingOlderMessages: threadHistoryQuery.isFetchingNextPage,
		isThreadLoading: threadHistoryQuery.isLoading,
	};
};

export default useStaffCommunication;
